import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAPI } from './useAPIs';

const normalizeStatus = (status) => String(status || '').toLowerCase();

const isStatusSuccess = (status) => {
  if (!status) return false;
  const s = normalizeStatus(status);
  return s === 'success' || s === 'ready';
};

const isStatusFailure = (status) => {
  if (!status) return false;
  const s = normalizeStatus(status);
  return s === 'failure' || s === 'failed';
};

const getErrorMessage = (error, fallback = 'An unexpected error occurred') => {
  const apiError = error?.response?.data;
  return apiError?.error || apiError?.detail || error?.message || fallback;
};

const isStatusTerminal = (status) => isStatusSuccess(status) || isStatusFailure(status);

const resolveActiveStep = (isReady, isError, statusData, startData) => {
  if (isReady) return 7;
  if (isError) return -1;

  const message =
    statusData?.message?.toLowerCase() || startData?.message?.toLowerCase() || '';
  const progress = statusData?.progress;

  if (message.includes('extracting signal')) return 6;
  if (
    message.includes('caching to azure') ||
    message.includes('caching results') ||
    message.includes('saving signal') ||
    message.includes('signal list to cache')
  )
    return 5;
  if (message.includes('reading processed')) return 4;
  if (
    message.includes('decoding can') ||
    message.includes('decoding lin') ||
    message.includes('decoding signals') ||
    message.includes('decompressing') ||
    message.includes('transform')
  )
    return 3;
  if (message.includes('loading arxml') || message.includes('arxml database'))
    return 2;
  if (
    message.includes('downloading') ||
    message.includes('download file') ||
    message.includes('loading cached') ||
    message.includes('cached parquet')
  )
    return 1;
  if (
    message.includes('checking cache') ||
    message.includes('preparing temporary') ||
    message.includes('start') ||
    message.includes('initializ')
  )
    return 0;

  if (progress !== undefined && progress !== null) {
    if (progress >= 95) return 6;
    if (progress >= 90) return 5;
    if (progress >= 85) return 4;
    if (progress >= 15) return 3;
    if (progress >= 10) return 2;
    if (progress >= 5) return 1;
  }

  return 0;
};

export const PREPARATION_STEPS = [
  { label: 'Cache Check', description: 'Checking cache...' },
  { label: 'Downloading', description: 'Downloading file from Azure storage' },
  { label: 'ARXML Loading', description: 'Loading ARXML database...' },
  { label: 'Decoding', description: 'Decoding CAN/LIN signals...' },
  { label: 'Reading Output', description: 'Reading processed parquet...' },
  { label: 'Caching', description: 'Caching to Azure Blob...' },
  { label: 'Extracting Signals', description: 'Extracting signal list...' },
  { label: 'Ready', description: 'File is ready' },
];

export const useBuslogPrepare = (filePath, { enabled = true } = {}) => {
  const { apiCall } = useAPI();
  const queryClient = useQueryClient();

  const callPreparePost = useCallback(async () => {
    const response = await apiCall('POST', '/api/v1/signals/prepare/', {
      file_path: filePath,
      category: 'buslog',
    });

    const postData = response?.data || {};
    const postStatus = normalizeStatus(postData?.status);

    if (
      postStatus === 'ready' ||
      postStatus === 'processing' ||
      postStatus === 'success' ||
      response?.status === 200 ||
      response?.status === 202
    ) {
      return postData;
    }

    throw new Error('Unexpected response from prepare POST endpoint');
  }, [apiCall, filePath]);

  const {
    data: startData,
    isError: isStartError,
    error: startError,
    isPending: isStarting,
    refetch: refetchStart,
  } = useQuery({
    queryKey: ['prepareFileStart', filePath],
    queryFn: async () => {
      try {
        const getResponse = await apiCall('GET', '/api/v1/signals/prepare/', {
          params: {
            file_path: filePath,
            category: 'buslog',
          },
        });

        const getData = getResponse?.data || {};
        const getStatus = normalizeStatus(getData?.status);

        if (getStatus === 'ready' || getStatus === 'processing') {
          return getData;
        }

        if (getStatus === 'not_started' || getStatus === 'parquet_cached') {
          return await callPreparePost();
        }

        if (
          getResponse?.status === 200 &&
          (getData?.signal_count !== undefined || getData?.result?.signal_count !== undefined)
        ) {
          return { ...getData, status: 'ready' };
        }

        return await callPreparePost();
      } catch (error) {
        const statusCode = error?.response?.status;

        if (statusCode === 404 || statusCode === 405) { // If not found or method not allowed, assume prepare hasn't been started and try POST
          return await callPreparePost();
        }

        throw new Error(getErrorMessage(error, 'Failed to prepare file'));
      }
    },
    enabled: !!filePath && enabled,
    staleTime: Infinity,
    retry: false,
  });

  const taskId = startData?.task_id;
  const startStatus = normalizeStatus(startData?.status);
  const isImmediateReady = isStatusSuccess(startStatus);
  const isProcessingWithoutTaskId = startStatus === 'processing' && !taskId;

  const {
    data: statusData,
    isError: isPollError,
    error: pollError,
  } = useQuery({
    queryKey: ['prepareFileStatus', taskId],
    queryFn: async () => {
      const response = await apiCall(
        'GET',
        `/api/v1/signals/tasks/${taskId}/`
      );
      return response.data;
    },
    enabled: !!taskId && !isImmediateReady && enabled,
    refetchInterval: (query) => {    // Poll for processing status
      const data = query.state.data;
      if (isStatusTerminal(data?.status)) {
        return false;
      }
      return 2000;
    },
    refetchIntervalInBackground: true,
  });

  const resolvedSignalCount = useMemo(() => {
    const fromStatus = statusData?.signal_count ?? statusData?.result?.signal_count;
    const fromStart = startData?.signal_count ?? startData?.result?.signal_count;
    const count = fromStatus ?? fromStart;
    return count !== undefined ? count : null;
  }, [statusData, startData]);

  const rawReady = isImmediateReady || isStatusSuccess(statusData?.status);

  const isEmpty = rawReady && resolvedSignalCount === 0;

  const isFileReady = rawReady && !isEmpty;

  const isPreparing =
    isStarting ||
    (!!taskId &&
      !isImmediateReady &&
      !isStatusTerminal(statusData?.status)) ||
    (startStatus === 'processing' && !isImmediateReady && !statusData && !isProcessingWithoutTaskId);

  const isPrepError =
    isStartError ||
    isPollError ||
    isStatusFailure(statusData?.status) ||
    isProcessingWithoutTaskId;

  const prepError =
    (isProcessingWithoutTaskId
      ? new Error(startData?.message || 'File processing is in progress but no task id was returned')
      : null) ||
    startError ||
    pollError ||
    (isStatusFailure(statusData?.status)
      ? new Error(
          statusData?.error || statusData?.message || 'Processing failed'
        )
      : null);

  const signalCount = resolvedSignalCount;

  // Resolve start_ts and end_ts from the prepare / status response (assumed to be nanoseconds).
  // This is the authoritative total file duration — independent of which signals
  // the user has selected.
  const start_ts = useMemo(() => {
    const ns =
      statusData?.start_ts ??
      startData?.start_ts ??
      null;
    if (ns == null || ns <= 0) return 0;
    return ns;
  }, [statusData, startData]);

  const end_ts = useMemo(() => {
    const ns =
      statusData?.end_ts ??
      startData?.end_ts ??
      null;
    if (ns == null || ns <= 0) return 0;
    return ns;
  }, [statusData, startData]);

  const activeStep = useMemo(
    () => resolveActiveStep(isFileReady, isPrepError, statusData, startData),
    [isFileReady, isPrepError, statusData, startData]
  );

  const currentMessage = useMemo(
    () => statusData?.message || startData?.message || '',
    [statusData, startData]
  );

  const retry = useCallback(() => {
    queryClient.removeQueries({ queryKey: ['prepareFileStart', filePath] });
    queryClient.removeQueries({ queryKey: ['prepareFileStatus'] });
    queryClient.removeQueries({ queryKey: ['prepareFileStatus', taskId] });
    refetchStart();
  }, [queryClient, filePath, taskId, refetchStart]);

  return {
    // Status flags
    isFileReady,
    isPreparing,
    isPrepError,
    isEmpty,
    prepError,
    // Data
    taskId,
    statusData,
    startData,
    signalCount,
    start_ts,
    end_ts,
    progress: statusData?.progress ?? startData?.progress,
    // Stepper
    activeStep,
    currentMessage,
    steps: PREPARATION_STEPS,
    // Actions
    retry,
  };
};
